import { useEffect, useRef, useState } from 'react'
import { PageName } from 'web-event-collector'
import { useRouter } from 'next/router'

import Button from '@/components/atomic/atoms/AButton'
import Text from '@/components/atomic/atoms/Text'
import { HospitalList, HospitalListHeader, HospitalListItem } from '@/components/pages/hospitalList'
import { useScroll } from '@/hooks/Scroll'
import { useTranslation } from '@/lang'
import FlexRow from '@/components/shared/FlexRow'
import FlexCol from '@/components/shared/FlexCol'
import { HospitalSearchFilter } from '@/features/hospital/components/hospital-search-filter'
import { SearchHospital } from '@/features/hospital/types/hospital.type'

const MARGIN_REACH_BOTTOM = 150

export interface Props {
  userId: number
  hospitals: SearchHospital[]
  totalHospitalCount: number
  loadMoreHospitals: () => Promise<void>
  hasMoreHospitals: boolean
  onClickMore: () => void
}

const HospitalListTemplate = ({
  userId,
  totalHospitalCount,
  hospitals,
  loadMoreHospitals,
  hasMoreHospitals,
}: Props): JSX.Element => {
  const { t } = useTranslation()
  const router = useRouter()

  const { initScroll, scrollTo, subscribe, unsubscribe } = useScroll()
  const isLoading = useRef<boolean>(false)
  const autoScrollRef = useRef<HTMLDivElement>(null)

  const [isLoadMoreAvailable, setIsLoadMoreAvailable] = useState(true)
  const loadMore = async () => {
    if (isLoading.current || !hasMoreHospitals) {
      return
    }
    isLoading.current = true
    try {
      await loadMoreHospitals()
    } finally {
      isLoading.current = false
    }
  }

  useEffect(() => {
    setIsLoadMoreAvailable(hospitals.length !== 0 && hasMoreHospitals)
  }, [hospitals, hasMoreHospitals])

  useEffect(() => {
    scrollTo(0)
  }, [router.query])

  useEffect(() => {
    initScroll()
    const onScroll = (e: { scrollY: number }) => {
      const body = document.body
      const isReachedToBottom =
        body &&
        autoScrollRef.current &&
        e.scrollY + body.clientHeight - MARGIN_REACH_BOTTOM > autoScrollRef.current.offsetTop

      if (isReachedToBottom) {
        loadMore()
      }
    }

    subscribe('scroll', onScroll)

    return () => {
      unsubscribe('scroll', onScroll)
    }
  }, [hospitals])

  const hasSearchKeyword = router.query?.q
  const isInfiniteScrollActivated = hospitals?.length > 20

  return (
    <FlexCol className="pb-12">
      {!hasSearchKeyword && <HospitalListHeader totalHospitalCount={totalHospitalCount} />}
      <HospitalSearchFilter />
      {hasSearchKeyword && (
        <FlexRow justify="center" items="start" px={16} pt={20} pb={12}>
          <Text type="title3" element="h1">
            {t('search.title', { keyword: router.query.q })}
            <Text type="title3" element="span" color="#FF540F">
              {` ${totalHospitalCount.toLocaleString()}`}
            </Text>
            {t('common.unit.length')}
          </Text>
        </FlexRow>
      )}
      <HospitalList>
        {hospitals
          .filter((hospital) => hospital.mainImage)
          .map((hospital: any, index: number) => (
            <HospitalListItem
              key={`${hospital.id}_${index}`}
              hospital={hospital}
              userId={userId}
              pageName={PageName.HospitalListWeb}
              indexInList={index}
              meta={{ pageIndex: 0 }}
            />
          ))}
      </HospitalList>
      {isLoadMoreAvailable && (
        <LoadMoreSection
          isLoading={isLoading.current}
          isInfiniteScrollActivated={isInfiniteScrollActivated}
          autoScrollRef={autoScrollRef}
          onLoadMore={loadMore}
        />
      )}
    </FlexCol>
  )
}

interface LoadMoreSectionProps {
  isLoading: boolean
  isInfiniteScrollActivated: boolean
  autoScrollRef: React.RefObject<HTMLDivElement>
  onLoadMore: () => void
}

function LoadMoreSection({ isLoading, isInfiniteScrollActivated, autoScrollRef, onLoadMore }: LoadMoreSectionProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Text type="caption1" element="p" color="#767676" className="text-center">
        {t('components.moreInfo.contents.text')}
      </Text>
    )
  }

  if (isInfiniteScrollActivated) {
    return <div ref={autoScrollRef} />
  }

  return (
    <FlexRow justify="center" items="center" px={16} pt={12}>
      <Button className="w-full" color="primary" size="medium" onClick={onLoadMore}>
        {t('components.moreButton.contents.text')}
      </Button>
    </FlexRow>
  )
}

export default HospitalListTemplate
